import { apiClient } from './apiClient';
import { camelize } from './_caseTransform';
import type { Environment, EnvironmentCreate, EnvironmentUpdate, EnvironmentWithStats } from '../types/environment';

/**
 * Environments are org-scoped in the API (moved from project-scoped in the
 * ProjectEnvironment refactor). A Project × Environment pair is materialized
 * as a ProjectEnvironment; see projectEnvironmentService for that join.
 *
 * API endpoints:
 *   GET    /environments/?organization_id=
 *   GET    /environments/{id}
 *   POST   /environments/
 *   DELETE /environments/{id}
 * PUT is not exposed by the API; update() only works against mock storage.
 */

const createMockEnvironment = (data: EnvironmentCreate): Environment => ({
  id: crypto.randomUUID(),
  name: data.name,
  label: data.label,
  organizationId: data.organizationId,
  createdByUserId: 'current-user',
  createdAt: new Date().toISOString(),
  updatedByUserId: 'current-user',
  updatedAt: new Date().toISOString(),
});

const STORAGE_KEY = 'gizmodata-environments';

const getStoredEnvironments = (): Environment[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const storeEnvironments = (environments: Environment[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(environments));
};

export const environmentService = {
  /** Get all environments for an organization. */
  async getEnvironments(organizationId: string): Promise<Environment[]> {
    const mockEnvironments = getStoredEnvironments().filter(e => e.organizationId === organizationId);

    const response = camelize(await apiClient.getAll<Environment>(
      `/environments/?organization_id=${organizationId}`,
      mockEnvironments,
    ));
    return response.data;
  },

  async getEnvironment(environmentId: string): Promise<Environment | null> {
    const mockEnvironment = getStoredEnvironments().find(e => e.id === environmentId);

    const response = camelize(await apiClient.get<Environment>(
      `/environments/${environmentId}`,
      mockEnvironment || undefined,
    ));
    return response.data;
  },

  async getEnvironmentWithStats(environmentId: string): Promise<EnvironmentWithStats | null> {
    const mockEnvironment = getStoredEnvironments().find(e => e.id === environmentId);
    const mockWithStats: EnvironmentWithStats | undefined = mockEnvironment
      ? {
          ...mockEnvironment,
          projectEnvironmentCount: 0,
          instanceCount: 0,
          runningClusterCount: 0,
          memberCount: 1,
        }
      : undefined;

    const response = camelize(await apiClient.get<EnvironmentWithStats>(
      `/environments/${environmentId}?expand=stats`,
      mockWithStats,
    ));
    return response.data;
  },

  async createEnvironment(data: EnvironmentCreate): Promise<Environment> {
    const mockEnvironment = createMockEnvironment(data);

    const response = camelize(await apiClient.post<Environment>(`/environments/`, data));

    if (response.source === 'mock') {
      const environments = getStoredEnvironments();
      environments.push(response.data);
      storeEnvironments(environments);
    }

    return response.data;
  },

  async updateEnvironment(environmentId: string, data: EnvironmentUpdate): Promise<Environment> {
    // No PUT endpoint on the API; this only persists for mock mode.
    const existingEnvironments = getStoredEnvironments();
    const existingIndex = existingEnvironments.findIndex(e => e.id === environmentId);
    const mockUpdated =
      existingIndex >= 0
        ? {
            ...existingEnvironments[existingIndex],
            ...data,
            updatedAt: new Date().toISOString(),
          }
        : undefined;

    const response = camelize(await apiClient.put<Environment>(`/environments/${environmentId}`, data));

    if (response.source === 'mock' && existingIndex >= 0) {
      existingEnvironments[existingIndex] = response.data;
      storeEnvironments(existingEnvironments);
    }

    return response.data;
  },

  async deleteEnvironment(environmentId: string): Promise<void> {
    await apiClient.delete<void>(`/environments/${environmentId}`, undefined);

    const environments = getStoredEnvironments().filter(e => e.id !== environmentId);
    storeEnvironments(environments);
  },
};
